<template>
  <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
    <!-- Profile Header with Background -->
    <div
      class="relative h-48 sm:h-40 bg-gradient-to-r from-primary to-primary/70"
    >
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-100">
        <div
          class="absolute -bottom-8 left-1/2 sm:left-6 transform -translate-x-1/2 sm:translate-x-0 flex items-end z-10"
        >
          <div
            class="w-28 h-28 sm:w-24 sm:h-24 rounded-full bg-white dark:bg-gray-800 p-1.5 shadow-lg ring-4 ring-white dark:ring-gray-800"
          >
            <div
              class="w-full h-full rounded-full bg-primary/10 flex items-center justify-center relative group"
            >
              <span
                class="text-4xl sm:text-3xl font-semibold text-primary group-hover:scale-110 transition-transform"
              >
                {{ profile?.first_name?.[0] || "?" }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Content -->
    <div class="relative pt-16 sm:pt-12 px-4 sm:px-6 pb-6">
      <!-- Basic Info -->
      <div class="mb-8 text-center sm:text-left">
        <div class="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 mb-2">
          <h2
            class="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 group"
          >
            <Icon
              name="heroicons:academic-cap"
              class="w-6 h-6 text-primary hidden sm:block group-hover:rotate-12 transition-transform"
            />
            {{ profile?.title || "" }} {{ profile?.first_name || "" }}
            {{ profile?.last_name || "" }}
          </h2>
          <div
            class="flex items-center gap-2 flex-wrap justify-center sm:justify-start"
          >
            <span
              v-if="profile?.type"
              class="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full"
            >
              {{ profile.type }}
            </span>
            <span
              v-if="profile?.content_type"
              class="px-3 py-1 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full"
            >
              {{ profile.content_type }}
            </span>
          </div>
        </div>
        <p
          class="text-lg text-gray-600 dark:text-gray-400 flex items-center justify-center sm:justify-start gap-2 group"
        >
          <Icon
            name="heroicons:building-office-2"
            class="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors"
          />
          {{ profile?.institution || "Institution not specified" }}
        </p>
      </div>

      <!-- Quick Stats -->
      <div class="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div
          v-if="profile?.highest_degree?.name"
          class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between sm:flex-col sm:items-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
        >
          <h4
            class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
          >
            <Icon
              name="heroicons:academic-cap"
              class="w-4 h-4 group-hover:text-primary transition-colors"
            />
            Degree
          </h4>
          <p
            class="font-semibold text-gray-900 dark:text-white text-right sm:text-center"
          >
            {{ profile.highest_degree.name }}
          </p>
        </div>
        <div
          v-if="profile?.field_of_study"
          class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between sm:flex-col sm:items-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
        >
          <h4
            class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
          >
            <Icon
              name="heroicons:beaker"
              class="w-4 h-4 group-hover:text-primary transition-colors"
            />
            Field
          </h4>
          <p
            class="font-semibold text-gray-900 dark:text-white text-right sm:text-center"
          >
            {{ profile.field_of_study }}
          </p>
        </div>
        <div
          v-if="profile?.createdAt"
          class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between sm:flex-col sm:items-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
        >
          <h4
            class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
          >
            <Icon
              name="heroicons:clock"
              class="w-4 h-4 group-hover:text-primary transition-colors"
            />
            Member Since
          </h4>
          <p
            class="font-semibold text-gray-900 dark:text-white text-right sm:text-center"
          >
            {{ new Date(profile.createdAt).getFullYear() }}
          </p>
        </div>
        <div
          v-if="profile?.updatedAt"
          class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between sm:flex-col sm:items-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
        >
          <h4
            class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
          >
            <Icon
              name="heroicons:arrow-path"
              class="w-4 h-4 group-hover:text-primary transition-colors"
            />
            Last Updated
          </h4>
          <p
            class="font-semibold text-gray-900 dark:text-white text-right sm:text-center"
          >
            {{ new Date(profile.updatedAt).toLocaleDateString() }}
          </p>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Left Column -->
        <div class="space-y-6">
          <!-- About Section -->
          <div
            v-if="profile?.about"
            class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transform transition-all duration-300 hover:scale-[1.02] hover:bg-gray-100 dark:hover:bg-gray-700/70"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
            >
              <Icon name="heroicons:user-circle" class="w-5 h-5 text-primary" />
              About
            </h3>
            <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ profile.about }}
            </p>
          </div>

          <!-- Event Preferences -->
          <div
            v-if="profile?.preferred_event_types?.length"
            class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transform transition-all duration-300 hover:scale-[1.02] hover:bg-gray-100 dark:hover:bg-gray-700/70"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
            >
              <Icon name="heroicons:calendar" class="w-5 h-5 text-primary" />
              Event Preferences
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="type in profile.preferred_event_types"
                :key="type"
                class="px-3 py-1.5 text-sm bg-primary/5 text-primary rounded-full flex items-center gap-1.5 hover:bg-primary/10 transition-colors cursor-default"
              >
                <Icon name="heroicons:star" class="w-4 h-4" />
                {{ type?.name }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-6">
          <!-- Social Links -->
          <div
            v-if="profile?.linkedin_profile || profile?.twitter_profile"
            class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transform transition-all duration-300 hover:scale-[1.02] hover:bg-gray-100 dark:hover:bg-gray-700/70"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
            >
              <Icon name="heroicons:globe-alt" class="w-5 h-5 text-primary" />
              Connect
            </h3>
            <div class="space-y-3">
              <a
                v-if="profile?.linkedin_profile"
                :href="profile.linkedin_profile"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <Icon name="mdi:linkedin" class="w-6 h-6 text-[#0077B5]" />
                <span
                  class="text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors"
                  >LinkedIn Profile</span
                >
                <Icon
                  name="heroicons:arrow-top-right-on-square"
                  class="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </a>
              <a
                v-if="profile?.twitter_profile"
                :href="profile.twitter_profile"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <Icon name="mdi:twitter" class="w-6 h-6 text-[#1DA1F2]" />
                <span
                  class="text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors"
                  >Twitter Profile</span
                >
                <Icon
                  name="heroicons:arrow-top-right-on-square"
                  class="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </a>
            </div>
          </div>

          <!-- Other Information -->
          <div
            v-if="profile?.other"
            class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transform transition-all duration-300 hover:scale-[1.02] hover:bg-gray-100 dark:hover:bg-gray-700/70"
          >
            <h3
              class="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
            >
              <Icon
                name="heroicons:information-circle"
                class="w-5 h-5 text-primary"
              />
              Additional Information
            </h3>
            <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
              {{ profile.other }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ProfileAttributes {
  about: string | null;
  type: string | null;
  slug: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  full_name: string;
  institution: string;
  field_of_study: string | null;
  content_type: string | null;
  twitter_profile: string | null;
  linkedin_profile: string | null;
  other: string | null;
  highest_degree: string | null;
  contribute_content: boolean | null;
  preferred_event_types: string[];
  first_name: string;
  last_name: string;
  title: string;
}

defineProps<{
  profile: ProfileAttributes;
}>();
</script>
